// DOM-byggere. Ren TS, ingen rammeverk — samme stil som apps/cop.
//
// REGELEN SOM HOLDER HELE KLIENTEN: her finnes ingen `innerHTML`. All tekst
// settes med `textContent`, all struktur med `createElement`. Grunnen står i
// grunnloven (§4: «All feltavledet tekst escapes før DOM»), men den er også
// helt konkret her: kort-titler, økt-fritekst og merknader på registreringslenker
// er skrevet av mennesker og kan inneholde `<script>`. Og fordi persontokenet
// ligger i nettleserlagringen (se lib/token-lager.ts), ville én XSS-feil gitt
// bort tokenet. `src/ingen-innerhtml.test.ts` håndhever regelen på hele src/.
import { ikon } from './ikoner.js';

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls = '', tekst?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (tekst !== undefined) e.textContent = tekst;
  return e;
}

export const div = (cls = '', tekst?: string) => el('div', cls, tekst);
export const span = (cls = '', tekst?: string) => el('span', cls, tekst);
export const p = (cls = '', tekst?: string) => el('p', cls, tekst);
export const h = (nivå: 1 | 2 | 3, cls = '', tekst?: string) => el(`h${nivå}` as 'h1', cls, tekst);

/** Tømmer en node uten `innerHTML = ''`. */
export function tøm(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/** Bytt hele innholdet i en node. */
export function sett(node: Node, ...barn: (Node | null | undefined)[]): void {
  tøm(node);
  for (const b of barn) if (b) node.appendChild(b);
}

export interface KnappValg {
  variant?: 'primær' | 'stille' | 'fare' | 'lenke';
  tittel?: string;
  ikon?: SVGElement;
  type?: 'button' | 'submit';
  deaktivert?: boolean;
}

export function knapp(tekst: string, påKlikk?: (e: MouseEvent) => void, valg: KnappValg = {}): HTMLButtonElement {
  const b = el('button', `pk pk-${valg.variant ?? 'stille'}`);
  b.type = valg.type ?? 'button';
  if (valg.ikon) b.appendChild(valg.ikon);
  if (tekst) b.appendChild(span('pk-tekst', tekst));
  // Tittel bare når den sier noe MER enn etiketten. En tooltip som gjentar
  // knappeteksten er støy for alle og et dobbelt opplest ord for skjermleseren.
  if (valg.tittel && valg.tittel !== tekst) b.title = valg.tittel;
  if (!tekst && valg.tittel) b.setAttribute('aria-label', valg.tittel);
  if (valg.deaktivert) b.disabled = true;
  if (påKlikk) b.addEventListener('click', påKlikk);
  return b;
}

/**
 * Kopier-knapp for en verdi som skal ut av skjermen (en lenke, et token).
 *
 * Utklippstavla kan være blokkert — usikker opprinnelse, avslått rettighet, eller
 * en nettleser uten `navigator.clipboard`. Da SIER knappen det, framfor å se
 * vellykket ut mens ingenting ble kopiert: verdien står synlig ved siden av og kan
 * merkes for hånd. Det er hele grunnen til at denne finnes én gang og ikke i hver
 * skjerm — det er lett å glemme feilgrenen.
 *
 * `si` er skallets kvittering (`Kontekst.kvitter`), så knappen ikke trenger å
 * kjenne den.
 */
export function kopierknapp(
  verdi: string,
  hva: string,
  si: (melding: string, variant?: 'ok' | 'feil') => void,
): HTMLButtonElement {
  return knapp('Kopiér', () => {
    const skriv = globalThis.navigator?.clipboard?.writeText;
    if (!skriv) {
      si('Nettleseren tillot ikke kopiering — merk teksten og kopiér for hånd.', 'feil');
      return;
    }
    skriv.call(globalThis.navigator.clipboard, verdi)
      .then(() => si(`${hva} er kopiert`))
      .catch(() => si('Kopiering ble avvist — merk teksten og kopiér for hånd.', 'feil'));
  }, { variant: 'stille', ikon: ikon('kopier') });
}

let feltTeller = 0;

export interface FeltValg {
  type?: string;
  plassholder?: string;
  verdi?: string;
  hjelp?: string;
  påkrevd?: boolean;
  /** `HTMLInputElement.autocomplete` er typet som `AutoFill` (et union av de
   *  gyldige tokenene), ikke `string` — en vilkårlig streng ville sluppet gjennom
   *  skrivefeil som nettleseren stille ignorerer. Bruk samme type som DOM-en. */
  autofullfør?: AutoFill;
  /** `inputmode` — «numeric» gir sifferpanel på mobil. */
  inndatamodus?: string;
}

export interface Felt {
  rot: HTMLElement;
  input: HTMLInputElement;
  settFeil(melding: string | null): void;
}

/** Etikett + kontroll + plass til feilmelding, koblet med id-er for skjermleser. */
export function felt(etikett: string, valg: FeltValg = {}): Felt {
  const id = `f${++feltTeller}`;
  const rot = div('felt');
  const lab = el('label', 'felt-etikett', etikett);
  lab.htmlFor = id;
  const input = el('input', 'felt-inn');
  input.id = id;
  input.type = valg.type ?? 'text';
  if (valg.plassholder) input.placeholder = valg.plassholder;
  if (valg.verdi !== undefined) input.value = valg.verdi;
  if (valg.påkrevd) input.required = true;
  if (valg.inndatamodus) input.setAttribute('inputmode', valg.inndatamodus);
  input.autocomplete = valg.autofullfør ?? 'off';

  const feilEl = div('felt-feil');
  feilEl.id = `${id}-feil`;
  // `role=alert` gjør at en feil som dukker opp etter innsending faktisk leses
  // opp. Uten den er skjemavalidering usynlig for en skjermleserbruker.
  feilEl.setAttribute('role', 'alert');
  feilEl.hidden = true;

  rot.append(lab, input);
  if (valg.hjelp) {
    const hj = div('felt-hjelp', valg.hjelp);
    hj.id = `${id}-hjelp`;
    input.setAttribute('aria-describedby', hj.id);
    rot.append(hj);
  }
  rot.append(feilEl);

  return {
    rot, input,
    settFeil(melding) {
      feilEl.hidden = !melding;
      feilEl.textContent = melding ?? '';
      input.classList.toggle('har-feil', !!melding);
      input.setAttribute('aria-invalid', melding ? 'true' : 'false');
    },
  };
}

export interface ValgFelt {
  rot: HTMLElement;
  select: HTMLSelectElement;
}

export function velger(etikett: string, valg: readonly { verdi: string; navn: string }[], nåværende?: string): ValgFelt {
  const id = `f${++feltTeller}`;
  const rot = div('felt');
  const lab = el('label', 'felt-etikett', etikett);
  lab.htmlFor = id;
  const s = el('select', 'felt-inn');
  s.id = id;
  for (const v of valg) {
    const o = el('option', '', v.navn);
    o.value = v.verdi;
    if (nåværende !== undefined && v.verdi === nåværende) o.selected = true;
    s.append(o);
  }
  rot.append(lab, s);
  return { rot, select: s };
}

export function tekstområde(etikett: string, plassholder = '', rader = 3): { rot: HTMLElement; ta: HTMLTextAreaElement } {
  const id = `f${++feltTeller}`;
  const rot = div('felt');
  const lab = el('label', 'felt-etikett', etikett);
  lab.htmlFor = id;
  const ta = el('textarea', 'felt-inn felt-ta');
  ta.id = id;
  ta.rows = rader;
  if (plassholder) ta.placeholder = plassholder;
  rot.append(lab, ta);
  return { rot, ta };
}

/** Avkrysning med etikett til høyre. */
export function avkrysning(etikett: string, avkrysset = false): { rot: HTMLElement; input: HTMLInputElement } {
  const id = `f${++feltTeller}`;
  const rot = el('label', 'kryss');
  rot.htmlFor = id;
  const input = el('input');
  input.type = 'checkbox';
  input.id = id;
  input.checked = avkrysset;
  rot.append(input, span('kryss-tekst', etikett));
  return { rot, input };
}

/** Liten merkelapp: status, kilde, tilhørighet. */
export function merke(tekst: string, variant = ''): HTMLElement {
  return span(`merke${variant ? ` merke-${variant}` : ''}`, tekst);
}

/** Nøkkel/verdi-rad. */
export function nv(nøkkel: string, verdi: string): HTMLElement {
  const rad = div('nv');
  rad.append(span('nv-k', nøkkel), span('nv-v', verdi));
  return rad;
}
